import type FileSystemItem from 'devextreme/file_management/file_system_item';
import { apiFetch, readErrorMessage } from '../api/client';
import { withLoadingPanel } from '../loading/loadingPanel';
import { downloadZipUrl } from './fileBrowserApi';import { parseItemPathForRoutes, type MountRoute } from './createFileBrowserProvider';
import { defaultArchiveName, normalizeArchiveName } from './fileBrowserZip';

export interface DownloadZipRequest {
  mountId: string;
  sources: string[];
  archiveName: string;
}

export function buildDownloadZipRequest(
  items: readonly FileSystemItem[],
  routes: readonly MountRoute[]
): DownloadZipRequest {
  if (items.length === 0) {
    throw new Error('Select one or more items to download.');
  }

  let mountId: string | null = null;
  const sources: string[] = [];

  for (const item of items) {
    const parsed = parseItemPathForRoutes(item, routes);
    if (!parsed?.relative) {
      throw new Error('Storage locations cannot be downloaded as files.');
    }

    if (mountId === null) {
      mountId = parsed.mount.id;
    } else if (mountId !== parsed.mount.id) {
      throw new Error('Selected items must be from the same storage location.');
    }

    sources.push(parsed.relative);
  }

  return {
    mountId: mountId!,
    sources,
    archiveName: normalizeArchiveName(defaultArchiveName(items))
  };
}

export async function downloadItemsAsZip(
  mountId: string,
  sources: string[],
  archiveName: string
): Promise<void> {
  await withLoadingPanel('Preparing download…', async () => {
    const res = await apiFetch(downloadZipUrl(mountId), {
      method: 'POST',
      body: JSON.stringify({ sources, archiveName })
    });

    if (!res.ok) {
      throw new Error(await readErrorMessage(res));
    }

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = archiveName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  });
}